import React, { useEffect, useState } from "react";
import {
   LineChart,
   Line,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
   ResponsiveContainer
} from "recharts";
import Widget from "../../Widget/Widget";
import SettingsModal from "../../Modal/SettingsModal";
import NewsletterGraphModal from "../WidgetsSettings/newsletterGraphSettings/NewsletterGraphModal";
import { getListV3 } from "../../../services/restAPI/ListsV2";
import { Spin } from "../../BP2Components";
import "../WidgetsSettings/newsletterGraphSettings/NewsletterGraphStyle.less";

const DEFAULT_COLORS = ["#0B9055", "#0C5B3A", "#1D64AA", "#B49133", "#F5A596", "#78273A"];

const NewsletterGraph = ({ value, role }) => {
   const [loading, setLoading] = useState(false);
   const [data, setData] = useState([]);
   const [widgetSettings, setWidgetSettings] = useState(value);

   useEffect(() => {
      setWidgetSettings(value);
      if (value.listId) {
         setWidgetSettings(prev => ({ ...prev, refreshTrigger: Date.now() }));
      }
   }, [value]);

   useEffect(() => {
      const fetchData = async () => {
         if (!widgetSettings.listId || !widgetSettings.refreshTrigger) {
            setLoading(false);
            return;
         }
         setLoading(true);
         try {
            const response = await getListV3(widgetSettings.listId, 1, 100);
            if (response && response.data && response.data.data) {
               const rows = response.data.data;
               const columns = response.data.columns;

               const processedData = rows.map(row => {
                  const dataPoint: any = {};
                  // Find X axis value
                  const xColumn = columns.find(col => String(col.meta?.hid) === String(widgetSettings.axisXHumanName));
                  const xVal = xColumn ? row[xColumn.label] : null;
                  dataPoint.name = (xVal !== null && typeof xVal === 'object' ? xVal.value : xVal) ?? "???";

                  // Find values for each serie
                  widgetSettings.series?.forEach(serie => {
                     const serieKey = serie.label || serie.humanId;
                     if (!serieKey) return;
                     
                     const serieColumn = columns.find(col => String(col.meta?.hid) === String(serie.humanId));
                     
                     if (serieColumn) {
                        const val = row[serieColumn.label];
                        const extractedVal = (val !== null && typeof val === 'object') ? val.value : val;
                        dataPoint[serieKey] = (extractedVal !== null && extractedVal !== undefined) ? Number(extractedVal) : 0;
                     } else {
                        dataPoint[serieKey] = 0;
                     }
                  });
                  return dataPoint;
               });
               setData(processedData);
            }
         } catch (error) {
            console.error("Error fetching newsletter graph data:", error);
         } finally {
            setLoading(false);
         }
      };

      fetchData();
   }, [widgetSettings.refreshTrigger]);

   return (
      <div className="newsletterGraphWidget">
         <Widget
            styleName="gx-m-0"
            bgColor={widgetSettings.backgroundColourHex}
            role={role}
            extra={
               (role === "OWNER" || role === "MODERATOR") && (
                  <SettingsModal
                     widgetId={widgetSettings.id}
                     widgetName={widgetSettings.name}
                     icon="setting"
                     deleteIcon="true"
                     children={
                        <NewsletterGraphModal
                           value={widgetSettings}
                           setWidgetSettings={setWidgetSettings}
                        />
                     }
                  />
               )
            }
         >
            <div className="newsletter-graph-container">
               <div className="newsletter-graph-header">
                  <div className="newsletter-graph-title-container">
                     {widgetSettings.imageUrl && (
                        <img src={widgetSettings.imageUrl} alt="" className="newsletter-graph-icon" />
                     )}
                     <span className="newsletter-graph-title">{widgetSettings.titleKey}</span>
                  </div>
               </div>
               {loading ? (
                  <div className="gx-d-flex gx-justify-content-center gx-align-items-center" style={{ height: "300px" }}>
                     <Spin />
                  </div>
               ) : (
                  <div style={{ width: '100%', height: 400 }}>
                     <ResponsiveContainer>
                        <LineChart
                           data={data}
                           margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                           <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#e8e8e8" />
                           <XAxis 
                              dataKey="name" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#999', fontSize: 12 }}
                              dy={10}
                           />
                           <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#999', fontSize: 12 }}
                           />
                           <Tooltip />
                           <Legend 
                              verticalAlign="bottom" 
                              align="center" 
                              iconType="circle"
                              wrapperStyle={{ paddingTop: "20px" }}
                           />
                           {widgetSettings.series?.map((serie, index) => (
                              <Line
                                 key={index}
                                 type="monotone"
                                 dataKey={serie.label || serie.humanId}
                                 stroke={serie.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                                 strokeWidth={2}
                                 dot={(props) => {
                                    const { index: dotIndex, cx, cy } = props;
                                    if (dotIndex % 10 === 0) {
                                       return (
                                          <circle
                                             key={`dot-${dotIndex}`}
                                             cx={cx}
                                             cy={cy}
                                             r={4}
                                             fill="#fff"
                                             stroke={serie.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                                             strokeWidth={2}
                                          />
                                       );
                                    }
                                    return null;
                                 }}
                                 activeDot={{ r: 6 }}
                                 connectNulls
                              />
                           ))}
                        </LineChart>
                     </ResponsiveContainer>
                  </div>
               )}
            </div>
         </Widget>
      </div>
   );
};

export default NewsletterGraph;
